// A catalog item's lifecycle as the supplier sees it. The backend serves it on
// every item as `status`:
// - draft: not published yet (saved without a photo, or kept as a draft with
//   "Save draft"). Members never see it; it can't be paused or ordered.
//   Publishing it (it needs a photo) makes it active.
// - active: visible to members. Always has a main photo.
// - paused: hidden by the supplier. May lack a photo (legacy rows), in which
//   case it needs one before it can go live again.

export const PRODUCT_STATUS = {
  DRAFT: "draft",
  ACTIVE: "active",
  PAUSED: "paused",
};

/**
 * The item's status. Falls back to `is_active` for a payload that doesn't
 * carry `status` yet (this FE can ship before the backend half): such an item
 * is never a draft.
 */
export const getProductStatus = (product) => {
  if (!product) return PRODUCT_STATUS.ACTIVE;
  const { status } = product;
  if (Object.values(PRODUCT_STATUS).includes(status)) return status;
  return product.is_active === false
    ? PRODUCT_STATUS.PAUSED
    : PRODUCT_STATUS.ACTIVE;
};

export const isDraftProduct = (product) =>
  getProductStatus(product) === PRODUCT_STATUS.DRAFT;

/**
 * True when the item can't go live until a photo is added: a draft without a
 * main image (one saved with "Save draft" may already have its photo), or a
 * paused item the server says has no valid main image. For a paused item only
 * an explicit `has_image === false` counts, so an older payload never shows
 * the prompt.
 */
export const needsPhotoToPublish = (product) => {
  const status = getProductStatus(product);
  if (status === PRODUCT_STATUS.DRAFT) return product?.has_image !== true;
  return status === PRODUCT_STATUS.PAUSED && product?.has_image === false;
};

/** A draft that already has its main photo: one click publishes it. */
export const isPublishableDraft = (product) =>
  getProductStatus(product) === PRODUCT_STATUS.DRAFT &&
  product?.has_image === true;

/** Machine-readable refusal codes the catalog API sends (`data.code`). */
export const PRODUCT_ERROR_CODE = {
  IMAGE_REQUIRED: "IMAGE_REQUIRED",
  DRAFT_CANNOT_PAUSE: "DRAFT_CANNOT_PAUSE",
  PRODUCT_NOT_AVAILABLE: "PRODUCT_NOT_AVAILABLE",
};

/** The refusal code on an axios error, or null. */
export const getProductErrorCode = (error) => {
  const data = error?.response?.data;
  return data?.code ?? data?.details?.code ?? null;
};
